/**
 * One mailbox provider's connect card, driven by REAL connect state.
 *
 * Extracted here (rather than inside a wizard) because both onboarding flows
 * want the same card: the invited-user flow's PermissionsStep uses it today,
 * and the creator flow's StackStep — which currently renders a presentational
 * block with an unconditional "Connected" badge and no connect action at all —
 * is a separate follow-up that can swap in this component.
 *
 * The OAuth trip is a FULL-PAGE navigation: `useOAuthConnect` stashes
 * `window.location.pathname` in sessionStorage under `oauth_return_url`, and
 * `ProviderCallback` navigates back to it afterwards. So a caller standing on
 * `/join/:orgId/permissions` returns to that exact step with no extra wiring —
 * and any wizard draft kept in sessionStorage survives the round trip, while
 * the in-memory React Query cache does not (which is what we want: the mailbox
 * status is refetched fresh on return).
 */
import { useCallback, useState } from 'react';

import { ConnectedBadge } from '@/components/integrations/IntegrationCards';
import {
  GoogleBrandMark,
  MicrosoftBrandMark,
  ProviderLogoTile,
} from '@/components/integrations/ProviderBrandMarks';
import type { DataVisibility } from '@/components/integrations/VisibilityChoiceModal';
import { VisibilityChoiceModal } from '@/components/integrations/VisibilityChoiceModal';
import { useOAuthConnect } from '@/hooks/integrations/useOAuthConnect';
import { Button } from '@/lib/base/buttons/button';
import { HintText } from '@/lib/base/forms/hint-text';

export type MailboxProvider = 'google' | 'microsoft';

const PROVIDER_COPY = {
  google: { label: 'Gmail', integrationLabel: 'Gmail' },
  microsoft: { label: 'Outlook', integrationLabel: 'Outlook' },
} as const;

interface MailboxConnectCardProps {
  orgId: string | undefined;
  provider: MailboxProvider;
  /** Whether this provider already has a connected account on this org. */
  isConnected: boolean;
  /** Shown under the provider name once connected. */
  connectedEmail?: string;
}

export function MailboxConnectCard({
  orgId,
  provider,
  isConnected,
  connectedEmail,
}: MailboxConnectCardProps) {
  const { connect, isConnecting, error } = useOAuthConnect(orgId, provider);
  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false);
  const copy = PROVIDER_COPY[provider];

  const handleConnect = useCallback(() => {
    setIsVisibilityModalOpen(true);
  }, []);

  const handleConfirmVisibility = useCallback(
    (visibility: DataVisibility) => {
      setIsVisibilityModalOpen(false);
      void connect(visibility);
    },
    [connect]
  );

  return (
    <div
      data-testid={`mailbox-connect-card-${provider}`}
      className='rounded-lg border border-border-secondary p-4'
    >
      <div className='flex items-center gap-3'>
        <ProviderLogoTile>
          {provider === 'google' ? <GoogleBrandMark /> : <MicrosoftBrandMark />}
        </ProviderLogoTile>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-semibold text-[var(--color-text-primary)]'>
            {copy.label}
          </p>
          {isConnected && connectedEmail && (
            <p className='truncate text-sm text-[var(--color-text-secondary)]'>
              {connectedEmail}
            </p>
          )}
        </div>
        {isConnected ? (
          <ConnectedBadge />
        ) : (
          <Button
            size='sm'
            color='secondary'
            onClick={handleConnect}
            isDisabled={isConnecting}
            disabledTooltip={`Taking you to ${copy.label} to finish connecting.`}
          >
            {isConnecting ? 'Connecting…' : 'Connect'}
          </Button>
        )}
      </div>

      {/* Inline, not a toast: onboarding suppresses global toasts, and an error
          about this card belongs next to this card. */}
      {error && (
        <div role='alert' className='mt-3'>
          <HintText isInvalid showError>
            {error}
          </HintText>
        </div>
      )}

      <VisibilityChoiceModal
        isOpen={isVisibilityModalOpen}
        onOpenChange={setIsVisibilityModalOpen}
        onConfirm={handleConfirmVisibility}
        integrationLabel={copy.integrationLabel}
        isConfirming={isConnecting}
      />
    </div>
  );
}
